/**
 * Mass Index
 *
 * Identifies trend reversals by measuring the range between high and low.
 * When the Mass Index rises above 27 and drops back below 26.5,
 * it signals a reversal ("reversal bulge").
 *
 * Category: Momentum | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeEMA } from "./sma";

export const massIndexIndicator: IndicatorDefinition = {
  id: "massindex",
  name: "Mass Index",
  shortName: "MASS",
  category: "momentum",
  pane: "separate",
  params: [
    { key: "emaPeriod", label: "EMA Period", type: "number", default: 9, min: 2, max: 100 },
    { key: "sumPeriod", label: "Sum Period", type: "number", default: 25, min: 5, max: 100 },
    { key: "color", label: "Color", type: "color", default: "#607D8B" },
  ],
  plots: [
    { key: "mass", label: "Mass", color: "#607D8B", style: "line", lineWidth: 2, priceScaleId: "mass" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { emaPeriod, sumPeriod } = params;
    const data: { time: number; value: number }[] = [];

    const range = candles.map(c => c.high - c.low);
    const ema1 = computeEMA(range, emaPeriod);
    const ema2 = computeEMA(ema1.filter(v => !isNaN(v)), emaPeriod);

    // Ratio of single to double EMA
    const ratio: number[] = [];
    let ema2Idx = 0;
    for (let i = 0; i < ema1.length; i++) {
      if (!isNaN(ema1[i])) {
        if (ema2Idx < ema2.length && !isNaN(ema2[ema2Idx]) && ema2[ema2Idx] !== 0) {
          ratio.push(ema1[i] / ema2[ema2Idx]);
        }
        ema2Idx++;
      }
    }

    // Sum of ratios over sumPeriod
    const offset = candles.length - ratio.length;
    for (let i = sumPeriod - 1; i < ratio.length; i++) {
      let sum = 0;
      for (let j = i - sumPeriod + 1; j <= i; j++) sum += ratio[j];
      const idx = i + offset;
      if (idx < candles.length) {
        data.push({ time: candles[idx].time, value: sum });
      }
    }

    return [{ key: "mass", data }];
  },
};
